/**
 * FOS Embedding Canvas
 *
 * WebGL-powered scatter plot visualization using Pixi.JS for rendering
 * thousands of FOS decision points efficiently.
 *
 * Features:
 * - GPU-accelerated rendering for 7,700+ points
 * - Pan and zoom with mouse/trackpad
 * - Color coding by outcome, cluster, or product
 * - Point hover with tooltip
 * - Cluster label overlays
 * - Selection highlighting
 */

import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { Graphics, FederatedPointerEvent, DisplayObject } from 'pixi.js';
import { scaleLinear } from 'd3-scale';
import { zoomIdentity } from 'd3-zoom';
import { useFOSProjections } from '../../../hooks/useFOSExplorer';
import { useRecord } from '../../../hooks/useRecords';
import type { FOSProjectionPoint, FOSSignalProjectionPoint } from '../../../utils/api';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import IconWrapper from '../../IconWrapper';
import { FiZoomIn, FiZoomOut, FiMaximize2, FiTarget, FiMapPin } from 'react-icons/fi';
import { cn } from '../../../lib/utils';
import { RecordHoverCardContent } from '../../v4/records/components/RecordHoverCard';
import { renderFOSClusterLabels } from './fosClusterLabels';
import { getFOSPointColor } from './fosCanvasDrawing';
import { buildFOSDecisionCoordMap, renderFOSSignalPoints } from './fosSignalPoints';
import { useFOSCanvasStage } from './useFOSCanvasStage';

// TODO(v4): Move FOS Explorer canvas into the v4 namespace to align with Records UI.

interface FOSEmbeddingCanvasProps {
  width?: number;
  height?: number;
  colorBy?: 'outcome' | 'cluster' | 'product';
  onPointClick?: (decisionId: string) => void;
  onSignalClick?: (signalId: string) => void;
  signalReferrerPath?: string;
  highlightedDecisionId?: string | null;
  highlightedSignalId?: string | null;
  hoveredClusterId?: number | null;
  selectedClusterId?: number | null;
  onClusterHover?: (clusterId: number | null) => void;
  onClusterSelect?: (clusterId: number | null) => void;
  signalProjections?: FOSSignalProjectionPoint[] | null;
  showSignals?: boolean;
  className?: string;
}

interface TooltipData {
  x: number;
  y: number;
  point: FOSProjectionPoint;
}

interface SignalHoverState {
  signalId: string;
  x: number;
  y: number;
}

const FOSEmbeddingCanvas: React.FC<FOSEmbeddingCanvasProps> = ({
  width: propWidth,
  height: propHeight,
  colorBy = 'outcome',
  onPointClick,
  onSignalClick,
  signalReferrerPath,
  highlightedDecisionId,
  highlightedSignalId,
  hoveredClusterId,
  selectedClusterId,
  onClusterHover,
  onClusterSelect,
  signalProjections,
  showSignals = false,
  className,
}) => {
  const {
    containerRef,
    pointsContainerRef,
    signalsContainerRef,
    labelsContainerRef,
    transformRef,
    isInitialized,
    width,
    height,
    applyZoomTransform,
  } = useFOSCanvasStage({ width: propWidth, height: propHeight });
  const pointGraphicsRef = useRef<Map<string, Graphics>>(new Map());
  const signalGraphicsRef = useRef<Map<string, Graphics>>(new Map());

  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [hoveredSignal, setHoveredSignal] = useState<SignalHoverState | null>(null);

  const { data: projectionData, isLoading, error } = useFOSProjections();
  const hoverClearTimeoutRef = useRef<number | null>(null);
  const { data: hoveredRecord, isLoading: isHoverRecordLoading, error: hoverRecordError } = useRecord(
    hoveredSignal?.signalId || '',
    { enabled: !!hoveredSignal?.signalId }
  );
  const decisionCoordMap = useMemo(() => {
    return buildFOSDecisionCoordMap(projectionData?.points);
  }, [projectionData?.points]);

  const clearSignalHover = useCallback(() => {
    if (hoverClearTimeoutRef.current) {
      window.clearTimeout(hoverClearTimeoutRef.current);
      hoverClearTimeoutRef.current = null;
    }
    setHoveredSignal(null);
  }, []);

  const scheduleSignalHoverClear = useCallback(() => {
    if (hoverClearTimeoutRef.current) {
      window.clearTimeout(hoverClearTimeoutRef.current);
    }
    hoverClearTimeoutRef.current = window.setTimeout(() => {
      setHoveredSignal(null);
      hoverClearTimeoutRef.current = null;
    }, 120);
  }, []);

  const handleSignalHoverStart = useCallback((hover: SignalHoverState) => {
    if (hoverClearTimeoutRef.current) {
      window.clearTimeout(hoverClearTimeoutRef.current);
      hoverClearTimeoutRef.current = null;
    }
    setHoveredSignal(hover);
  }, []);

  const handleSignalHoverMove = useCallback((hover: SignalHoverState) => {
    setHoveredSignal((prev) => (
      prev && prev.signalId === hover.signalId
        ? hover
        : prev
    ));
  }, []);

  useEffect(() => {
    if (!showSignals) {
      clearSignalHover();
    }
  }, [showSignals, clearSignalHover]);

  useEffect(() => {
    return () => {
      if (hoverClearTimeoutRef.current) {
        window.clearTimeout(hoverClearTimeoutRef.current);
      }
    };
  }, []);

  // Create scales for mapping data coordinates to screen coordinates
  const scales = useMemo(() => {
    if (!projectionData?.bounds) return null;

    const { x_min, x_max, y_min, y_max } = projectionData.bounds;
    if (x_min === null || x_max === null || y_min === null || y_max === null) {
      return null;
    }

    const padding = 40;
    const xScale = scaleLinear()
      .domain([x_min, x_max])
      .range([padding, width - padding]);
    const yScale = scaleLinear()
      .domain([y_min, y_max])
      .range([height - padding, padding]); // Inverted for screen coordinates

    return { x: xScale, y: yScale };
  }, [projectionData?.bounds, width, height]);

  // Render points when data changes
  useEffect(() => {
    if (!isInitialized || !projectionData || !scales || !pointsContainerRef.current) {
      return;
    }

    const container = pointsContainerRef.current;
    container.removeChildren();
    pointGraphicsRef.current.clear();

    const { points } = projectionData;

    points.forEach((point) => {
      const x = scales.x(point.x);
      const y = scales.y(point.y);
      const color = getFOSPointColor(point, colorBy);

      const graphics = new Graphics();
      graphics.beginFill(color, 0.7);
      graphics.drawCircle(x, y, 3);
      graphics.endFill();

      // Make interactive (v7 API)
      graphics.interactive = true;
      graphics.cursor = 'pointer';

      graphics.on('pointerover', (e: FederatedPointerEvent) => {
        graphics.clear();
        graphics.beginFill(color, 1);
        graphics.lineStyle(2, 0x000000);
        graphics.drawCircle(x, y, 5);
        graphics.endFill();

        const globalPos = e.global;
        setTooltip({ x: globalPos.x, y: globalPos.y, point });
      });

      graphics.on('pointerout', () => {
        graphics.clear();
        graphics.beginFill(color, 0.7);
        graphics.drawCircle(x, y, 3);
        graphics.endFill();
        setTooltip(null);
      });

      graphics.on('pointertap', () => {
        onPointClick?.(point.decision_id);
      });

      container.addChild(graphics as DisplayObject);
      pointGraphicsRef.current.set(point.decision_id, graphics);
    });
  }, [isInitialized, projectionData, scales, colorBy, onPointClick]);

  // Apply dim/highlight effect when hoveredClusterId or selectedClusterId changes
  useEffect(() => {
    if (!isInitialized || !projectionData || !scales || !pointsContainerRef.current) return;

    const { points } = projectionData;

    // Determine which cluster to highlight (hover takes precedence)
    const activeClusterId = hoveredClusterId ?? selectedClusterId;

    points.forEach((point) => {
      const graphics = pointGraphicsRef.current.get(point.decision_id);
      if (!graphics) return;

      const x = scales.x(point.x);
      const y = scales.y(point.y);
      const color = getFOSPointColor(point, colorBy);

      // Determine if this point should be highlighted, dimmed, or normal
      const isInActiveCluster = activeClusterId !== null && point.cluster_id === activeClusterId;
      const shouldDim = activeClusterId !== null && point.cluster_id !== activeClusterId;

      graphics.clear();

      if (isInActiveCluster) {
        // Highlight: full opacity, slightly larger
        graphics.beginFill(color, 1);
        graphics.drawCircle(x, y, 4);
        graphics.endFill();
      } else if (shouldDim) {
        // Dim: reduced opacity
        graphics.beginFill(color, 0.12);
        graphics.drawCircle(x, y, 2.5);
        graphics.endFill();
      } else {
        // Normal state
        graphics.beginFill(color, 0.7);
        graphics.drawCircle(x, y, 3);
        graphics.endFill();
      }
    });
  }, [hoveredClusterId, selectedClusterId, isInitialized, projectionData, scales, colorBy]);

  // Render cluster labels with background pills for legibility
  useEffect(() => {
    if (!isInitialized || !projectionData || !scales || !labelsContainerRef.current) return;

    renderFOSClusterLabels({
      container: labelsContainerRef.current,
      clusters: projectionData.clusters,
      scales,
      hoveredClusterId,
      selectedClusterId,
      onClusterHover,
      onClusterSelect,
    });
  }, [isInitialized, projectionData, scales, hoveredClusterId, selectedClusterId, onClusterHover, onClusterSelect]);

  // Render signal points when showSignals is enabled
  useEffect(() => {
    if (!isInitialized || !scales || !signalsContainerRef.current) return;

    renderFOSSignalPoints({
      container: signalsContainerRef.current,
      signalGraphicsById: signalGraphicsRef.current,
      signalProjections,
      showSignals,
      scales,
      decisionCoordMap,
      onSignalClick,
      onSignalHoverStart: handleSignalHoverStart,
      onSignalHoverMove: handleSignalHoverMove,
      onSignalHoverEnd: scheduleSignalHoverClear,
      onInvalidSignal: (signal) => {
        console.warn('[FOSCanvas] Skipping signal with invalid projection coords', signal);
      },
    });
  }, [
    isInitialized,
    scales,
    signalProjections,
    showSignals,
    decisionCoordMap,
    onSignalClick,
    handleSignalHoverStart,
    handleSignalHoverMove,
    scheduleSignalHoverClear,
  ]);

  // Highlight selected point
  useEffect(() => {
    if (!highlightedDecisionId || !scales || !projectionData) return;

    const point = projectionData.points.find((p) => p.decision_id === highlightedDecisionId);
    if (!point) return;

    const graphics = pointGraphicsRef.current.get(highlightedDecisionId);
    if (!graphics) return;

    const x = scales.x(point.x);
    const y = scales.y(point.y);
    const color = getFOSPointColor(point, colorBy);

    graphics.clear();
    graphics.beginFill(color, 1);
    graphics.lineStyle(3, 0x3b82f6);
    graphics.drawCircle(x, y, 6);
    graphics.endFill();

    return () => {
      graphics.clear();
      graphics.beginFill(color, 0.7);
      graphics.drawCircle(x, y, 3);
      graphics.endFill();
    };
  }, [highlightedDecisionId, scales, projectionData, colorBy]);

  const handleZoomIn = useCallback(() => {
    const currentK = transformRef.current.k;
    const newK = Math.min(currentK * 1.5, 10);

    applyZoomTransform(
      zoomIdentity
        .translate(transformRef.current.x, transformRef.current.y)
        .scale(newK)
    );
  }, [applyZoomTransform]);

  const handleZoomOut = useCallback(() => {
    const currentK = transformRef.current.k;
    const newK = Math.max(currentK / 1.5, 0.5);

    applyZoomTransform(
      zoomIdentity
        .translate(transformRef.current.x, transformRef.current.y)
        .scale(newK)
    );
  }, [applyZoomTransform]);

  const handleResetZoom = useCallback(() => {
    applyZoomTransform(zoomIdentity);
  }, [applyZoomTransform]);

  // Always render the container div so the ref is attached from first render
  // Show loading/error as overlays on top
  return (
    <div className={cn('relative', className)}>
      {/* Canvas container - ALWAYS rendered so ref is attached, fills available space */}
      <div
        ref={containerRef}
        className="bg-white overflow-hidden w-full h-full"
      />

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white">
          <Skeleton className="absolute inset-0" />
          <p className="relative text-sm text-muted-foreground">Loading projections...</p>
        </div>
      )}

      {/* Error overlay */}
      {error && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white">
          <div className="text-center py-8">
            <IconWrapper icon={FiTarget} className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-sm font-medium text-muted-foreground">Projections not available</p>
            <p className="text-xs text-muted-foreground mt-1">
              {error.message.includes('404')
                ? 'Run the compute_fos_projections task to generate visualization data.'
                : error.message}
            </p>
          </div>
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute top-3 right-3 flex flex-col gap-1">
        <Button variant="secondary" size="sm" onClick={handleZoomIn} className="h-8 w-8 p-0">
          <IconWrapper icon={FiZoomIn} className="h-4 w-4" />
        </Button>
        <Button variant="secondary" size="sm" onClick={handleZoomOut} className="h-8 w-8 p-0">
          <IconWrapper icon={FiZoomOut} className="h-4 w-4" />
        </Button>
        <Button variant="secondary" size="sm" onClick={handleResetZoom} className="h-8 w-8 p-0">
          <IconWrapper icon={FiMaximize2} className="h-4 w-4" />
        </Button>
      </div>

      {/* Stats badge */}
      {projectionData && (
        <div className="absolute bottom-3 left-3 flex gap-2">
          <Badge variant="secondary" className="text-xs">
            {projectionData.total_points.toLocaleString()} decisions •{' '}
            {projectionData.total_clusters} clusters
          </Badge>
          {showSignals && signalProjections && signalProjections.length > 0 && (
            <Badge variant="outline" className="text-xs bg-amber-50 border-amber-200 text-amber-700">
              <IconWrapper icon={FiMapPin} className="h-3 w-3 mr-1" />
              {signalProjections.length} signals
            </Badge>
          )}
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute pointer-events-none z-50 bg-white rounded-lg shadow-lg border p-2 text-xs max-w-[200px]"
          style={{
            left: tooltip.x + 10,
            top: tooltip.y - 10,
            transform: 'translateY(-100%)',
          }}
        >
          {/* Signal tooltip */}
          <>
            <div className="font-medium">{tooltip.point.decision_id.slice(0, 8)}...</div>
            {tooltip.point.outcome && (
              <div className="text-muted-foreground capitalize">
                {tooltip.point.outcome.replace('_', ' ')}
              </div>
            )}
            {tooltip.point.product_category && (
              <div className="text-muted-foreground">{tooltip.point.product_category}</div>
            )}
            {tooltip.point.total_compensation !== null && (
              <div className="text-green-600 font-medium">
                £{tooltip.point.total_compensation.toLocaleString()}
              </div>
            )}
          </>
        </div>
      )}

      {hoveredSignal && (
        <div
          className="absolute z-50 pointer-events-auto"
          style={{
            left: hoveredSignal.x + 12,
            top: hoveredSignal.y + 12,
          }}
          onMouseEnter={() => {
            if (hoverClearTimeoutRef.current) {
              window.clearTimeout(hoverClearTimeoutRef.current);
              hoverClearTimeoutRef.current = null;
            }
          }}
          onMouseLeave={scheduleSignalHoverClear}
        >
          <div className="bg-card border border-border rounded-lg shadow-lg w-[360px] overflow-hidden">
            {isHoverRecordLoading && (
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-full" />
              </div>
            )}
            {!isHoverRecordLoading && hoverRecordError && (
              <div className="p-4 text-xs text-muted-foreground">
                Unable to load signal preview.
              </div>
            )}
            {!isHoverRecordLoading && hoveredRecord && (
              <RecordHoverCardContent
                record={hoveredRecord}
                recordLink={`/v4/records/default/${hoveredRecord.id}`}
                referrerPath={signalReferrerPath}
              />
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default FOSEmbeddingCanvas;
